import { ComponentType, ElementType } from 'react';
import { styled } from '@activebrands/core-web/libs/styletron';
import { StyletronPropertyValue, StyletronStyle } from '@activebrands/core-web/types/styletron';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';

const StyledButton = styled('button', {
    whiteSpace: ['normal', 'nowrap'],
    overflowWrap: ['anywhere', 'normal'],
});

export interface ButtonThemeStyles {
    activeStyles?: StyletronStyle;
    disabledStyles?: StyletronStyle & { hoverStyles?: StyletronStyle };
    hoverStyles?: StyletronStyle;
    [key: string]: unknown;
}

export type ButtonSizeDimension = StyletronPropertyValue | StyletronPropertyValue[];

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    $style?: StyletronStyle;
    active?: boolean;
    as?: ElementType | string;
    availableSizes?: Record<string, Partial<Record<'height' | 'padding', ButtonSizeDimension>>>;
    disabled?: boolean;
    size?: string | (string | null)[];
    theme?: ButtonThemeStyles | string;
    type?: 'button' | 'reset' | 'submit';
}

const mapSize = (prop: 'height' | 'padding', obj: ButtonProps['availableSizes'], key: ButtonProps['size']) => {
    if (obj && key) {
        return Array.isArray(key) ? key.map(s => (s ? obj[s]?.[prop] : null)) : obj[key]?.[prop];
    }

    return null;
};

/* eslint-disable react/prop-types */
const Button: ComponentType<ButtonProps> = ({
    $style = {},
    active = false,
    as,
    availableSizes = {},
    disabled = false,
    size,
    theme = {},
    type = 'button',
    ...rest
}) => {
    const themeStyles = typeof theme === 'object' && theme !== null ? theme : {};
    const { activeStyles, hoverStyles, disabledStyles, ...restTheme } = themeStyles as ButtonThemeStyles;
    const activeStyle = active ? activeStyles : {};
    const disabledStyle = disabled ? disabledStyles : {};
    const disabledStyleHover = disabledStyles?.hoverStyles ?? {};

    const styles = {
        cursor: 'pointer',
        ...restTheme,
        padding: $style.padding || mapSize('padding', availableSizes, size),
        height: $style.height || mapSize('height', availableSizes, size),
        ':hover:not(:disabled):not(.disabled)': active ? {} : hoverStyles,
        ':disabled:hover': disabledStyleHover,
        ':hover.disabled': disabledStyleHover,
        ...activeStyle,
        ...disabledStyle,
        ...getFocusRingStyles(),
        ...$style,
    };

    return <StyledButton $as={as} $style={styles} disabled={disabled} type={!as ? type : undefined} {...rest} />;
};

export default Button;
